/**
 * Individual - Interface for named individuals (owl:NamedIndividual).
 *
 * Extends Node with individual-specific properties. Both TBox individuals
 * (defined in ontologies for enumerations/hasValue) and ABox individuals
 * (user-created instances) implement this interface.
 *
 * This provides a unified type for handling individuals across the application,
 * enabling features like object property dropdowns to work with both types.
 */

import type { Node } from "./Node.ts";

export interface Individual extends Node {
    /**
     * URIs of classes this individual is an instance of (rdf:type).
     * An individual can be an instance of multiple classes.
     */
    classUris: string[];

    /**
     * Check if this individual is an instance of a given class.
     * @param classUri The URI of the class to check
     * @returns true if this individual is an instance of the class
     */
    isInstanceOf(classUri: string): boolean;
}

/**
 * Type guard to check if a Node is an Individual.
 */
export function isIndividual(node: Node): node is Individual {
    return 'classUris' in node && 'isInstanceOf' in node;
}
